/**
 * Helper condivisi per le **Fonti di Livello** di una skill (`AssessmentView`),
 * usati sia dalla tabella interna completa ({@link SkillAssessmentTable}) sia
 * dalla vista CV condivisa ({@link ExternalCvProfileView}).
 *
 * Centralizzano due decisioni, così da mantenerle coerenti ovunque:
 *  - quante Fonti di Livello concorrono alla riga (il "numerino" accanto al
 *    badge, che apre lo schemino {@link LevelSourcesModal});
 *  - quale singolo Livello mostrare nel badge principale quando le fonti sono
 *    più d'una (si preferisce il proprio Self, con fallback alle altre fonti),
 *    così il Livello è **sempre** visibile se almeno una fonte esiste.
 */
import type { AssessmentView, Livello } from '../shared/dto';

/**
 * Numero di **Fonti di Livello** presenti sulla riga: Self, AI e i singoli voti
 * delle revisioni (reviewer + manager). È il conteggio mostrato nel contatore
 * accanto al badge del Livello: cliccandolo si apre lo schemino con tutti i
 * Livelli per fonte. Il Self conta `1` solo se dichiarato; l'AI `1` solo se
 * stimata; le revisioni per il numero di singoli voti (coerente con le righe
 * elencate da {@link LevelSourcesModal}).
 */
export function levelSourcesCount(row: AssessmentView): number {
  let count = 0;
  if (row.livelloSelf !== null) count += 1;
  if (row.aiLevel !== null) count += 1;
  // Una fonte per ogni singola revisione (un voto per valutatore): coerente con
  // le righe elencate da `LevelSourcesModal`.
  count += row.reviews.length;
  return count;
}

/**
 * Livello **esatto** da mostrare nel badge principale della colonna "Livello".
 *
 * Si preferisce il Livello auto‑dichiarato (`livelloSelf`), coerente con la
 * tabella interna. In sua assenza si ripiega su un Livello esatto proveniente
 * dalle revisioni, ma **solo** se i valutatori di un ruolo hanno votato tutti
 * lo stesso Livello (`reviewerUniformLevel`/`managerUniformLevel`): i ranghi
 * continui aggregati non sono un `Livello` esatto e non vengono quindi usati
 * qui (restano visibili nello schemino delle fonti). Da ultimo si usa l'`aiLevel`.
 *
 * Ritorna `null` solo quando nessuna fonte espone un Livello esatto; in tal
 * caso il badge mostra l'indicatore di assenza "—".
 */
export function primaryLivello(row: AssessmentView): Livello | null {
  if (row.livelloSelf !== null) return row.livelloSelf;
  if (row.reviewerUniformLevel !== null) return row.reviewerUniformLevel;
  if (row.managerUniformLevel !== null) return row.managerUniformLevel;
  return row.aiLevel;
}
